import React, { useState, useEffect } from "react";
import { db } from '../../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  Phone,
  MapPin,
  Mail,
  Clock,
  Send,
  MessageCircle,
  User,
  Calendar,
  Home,
  Users,
  FileText,
  Check,
  X,
} from "lucide-react";

export default function BookAppointment() {
  useEffect(() => {
    // Scroll to top when this page loads
    window.scrollTo(0, 0);
  }, []);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    serviceType: "",
    careFor: "",
    preferredDate: "",
    preferredTime: "",
    urgency: "",
    additionalInfo: "",
  });
  const [submitStatus, setSubmitStatus] = useState({
    show: false,
    success: false,
    message: "",
  });

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   try {
  //     const response = await fetch("/api/book-appointment", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(formData),
  //     });

  //     if (response.ok) {
  //       setSubmitStatus({
  //         show: true,
  //         success: true,
  //         message:
  //           "Appointment request submitted successfully! We will contact you shortly to confirm your appointment.",
  //       });
  //       // Clear form
  //       setFormData({
  //         firstName: "",
  //         lastName: "",
  //         email: "",
  //         phone: "",
  //         address: "",
  //         city: "",
  //         serviceType: "",
  //         careFor: "",
  //         preferredDate: "",
  //         preferredTime: "",
  //         urgency: "",
  //         additionalInfo: "",
  //       });
  //       // Hide message after 7 seconds
  //       setTimeout(() => {
  //         setSubmitStatus({ show: false, success: false, message: "" });
  //       }, 7000);
  //     } else {
  //       setSubmitStatus({
  //         show: true,
  //         success: false,
  //         message:
  //           "Failed to submit appointment. Please try again or call us directly at +1 604-722-6460.",
  //       });
  //     }
  //   } catch (error) {
  //     setSubmitStatus({
  //       show: true,
  //       success: false,
  //       message:
  //         "An error occurred. Please call us at +1 604-722-6460 to book your appointment.",
  //     });
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Save to Firestore
      await addDoc(collection(db, 'appointment-bookings'), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        serviceType: formData.serviceType,
        careFor: formData.careFor,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        urgency: formData.urgency,
        additionalInfo: formData.additionalInfo,
        submittedAt: serverTimestamp(),
        status: 'pending'
      });
  
      // Send email
      const response = await fetch("/api/book-appointment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
  
      if (response.ok) {
        setSubmitStatus({
          show: true,
          success: true,
          message: "Appointment request submitted successfully! We will contact you shortly to confirm your appointment.",
        });
        
        // Clear form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          address: "",
          city: "",
          serviceType: "",
          careFor: "",
          preferredDate: "",
          preferredTime: "",
          urgency: "",
          additionalInfo: "",
        });
        
        setTimeout(() => {
          setSubmitStatus({ show: false, success: false, message: "" });
        }, 7000);
      } else {
        setSubmitStatus({
          show: true,
          success: false,
          message: "Failed to submit appointment. Please try again or call us directly at +1 604 512 7113.",
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus({
        show: true,
        success: false,
        message: "An error occurred. Please call us at +1 604-512-7113 to book your appointment.",
      });
    }
  };
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const quickInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Call Us",
      details: "+1 604-512-7113",
      color: "#65c063",
      bgColor: "#dcfce7",
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Response Time",
      details: "Within 24 hours",
      color: "#f5a09e",
      bgColor: "#ffe8e6",
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Availability",
      details: "7 Days a Week",
      color: "#65c063",
      bgColor: "#dcfce7",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section
        className="relative py-20 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #f0fdf4 0%, #fff5f4 50%, #ffe8e6 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: "#65c063" }}
          ></div>
          <div
            className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: "#f5a09e" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <div className="inline-block mb-4">
            <span
              className="px-4 py-2 rounded-full text-sm font-medium text-white"
              style={{
                background: "linear-gradient(135deg, #65c063, #29a060)",
              }}
            >
              Book Your Appointment
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            Start Your Journey to
            <br />
            <span style={{ color: "#65c063" }}>Quality </span>
            <span style={{ color: "#f5a09e" }}>Care</span>
          </h1>

          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Schedule a free consultation with our care specialists. We'll
            discuss your needs and create a personalized care plan for your
            loved ones.
          </p>
        </div>
      </section>

      {/* Appointment Form Section */}
      <section
        className="py-20"
        style={{
          background: "linear-gradient(135deg, #fff5f4 0%, #f0fdf4 100%)",
        }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Appointment Request Form
              </h2>
              <p className="text-gray-600">
                Please fill out the form below and we'll contact you to confirm
                your appointment.
              </p>
            </div>

            {/* Success/Error Message */}
            {submitStatus.show && (
              <div
                className={`mb-6 p-4 rounded-2xl border-2 flex items-start gap-3 ${
                  submitStatus.success
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    submitStatus.success ? "bg-green-500" : "bg-red-500"
                  }`}
                >
                  {submitStatus.success ? (
                    <Check className="w-4 h-4 text-white" />
                  ) : (
                    <X className="w-4 h-4 text-white" />
                  )}
                </div>
                <p
                  className={`text-sm font-medium ${
                    submitStatus.success ? "text-green-800" : "text-red-800"
                  }`}
                >
                  {submitStatus.message}
                </p>
              </div>
            )}

            <div className="space-y-8">
              {/* Personal Information */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5" style={{ color: "#65c063" }} />
                  Personal Information
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                      placeholder="John"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                      placeholder="Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                        placeholder="+1 (604) 512-7113"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Home className="w-5 h-5" style={{ color: "#f5a09e" }} />
                  Location Information
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      required
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                      placeholder="Surrey"
                    />
                  </div>
                </div>
              </div>

              {/* Service Details */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" style={{ color: "#65c063" }} />
                  Service Details
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Service Type *
                    </label>
                    <select
                      name="serviceType"
                      required
                      value={formData.serviceType}
                      onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                    >
                      <option value="">Select a service</option>
                      <option value="personal-care">Personal Care</option>
                      <option value="companionship">Companionship</option>
                      <option value="homemaking">Homemaking</option>
                      <option value="respite-care">Respite Care</option>
                      <option value="meal-preparation">Meal Preparation</option>
                      <option value="multiple">Multiple Services</option>
                      <option value="multiple">Multiple Services</option>
                      <option value="dementia-care">Dementia Care</option>
                      <option value="palliative-care">Palliative Care</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Care is For *
                    </label>
                    <select
                      name="careFor"
                      required
                      value={formData.careFor}
                      onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                    >
                      <option value="">Select</option>
                      <option value="myself">Myself</option>
                      <option value="parent">Parent</option>
                      <option value="spouse">Spouse</option>
                      <option value="relative">Other Relative</option>
                      <option value="friend">Friend</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Scheduling Information */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" style={{ color: "#f5a09e" }} />
                  Preferred Schedule
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Preferred Date *
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      required
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Preferred Time *
                    </label>
                    <select
                      name="preferredTime"
                      required
                      value={formData.preferredTime}
                      onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                    >
                      <option value="">Select time</option>
                      <option value="morning">Morning (8AM - 12PM)</option>
                      <option value="afternoon">Afternoon (12PM - 5PM)</option>
                      <option value="evening">Evening (5PM - 8PM)</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Urgency *
                    </label>
                    <select
                      name="urgency"
                      required
                      value={formData.urgency}
                      onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors"
                    >
                      <option value="">Select</option>
                      <option value="immediate">Immediate (ASAP)</option>
                      <option value="this-week">This Week</option>
                      <option value="this-month">This Month</option>
                      <option value="planning">Just Planning</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Additional Information
                </label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-[#65c063] focus:outline-none transition-colors resize-none"
                  placeholder="Please share any specific needs, concerns, or questions you have about care services..."
                ></textarea>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleSubmit}
                  className="flex-1 text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all flex items-center justify-center gap-2"
                  style={{
                    background: "linear-gradient(135deg, #65c063, #29a060)",
                  }}
                >
                  <Send className="w-5 h-5" />
                  Submit Appointment Request
                </button>

                <a
                  href="tel:+16045127113"
                  className="flex-1 border-2 px-8 py-4 rounded-full font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  style={{ borderColor: "#f5a09e", color: "#f5a09e" }}
                >
                  <Phone className="w-5 h-5" />
                  Or Call Us Now
                </a>
              </div>

              <p className="text-sm text-gray-500 text-center">
                We'll review your request and contact you within 24 hours to
                confirm your appointment. For immediate assistance, please call
                us at +1 604-512-7113.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {quickInfo.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-6 rounded-2xl border-2 border-gray-100"
                style={{ backgroundColor: item.bgColor }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                >
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 font-medium">{item.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/16045127113"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform group"
        style={{ background: "#25D366" }}
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
        <span className="absolute right-16 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
          Chat with us
        </span>
      </a>
    </div>
  );
}
